import { useEffect, useState } from "react";
import { Menu, X, Phone, ArrowRight } from "lucide-react";
import { NAV_LINKS } from "@/data/content";

const Navbar = () => {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 12);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    document.body.style.overflow = open ? "hidden" : "";
  }, [open]);

  const close = () => setOpen(false);

  return (
    <header
      data-testid="navbar"
      className={`fixed top-0 inset-x-0 z-50 transition-all duration-300 ${
        scrolled ? "bg-white/90 backdrop-blur-md shadow-sm border-b border-border/60" : "bg-transparent"
      }`}
    >
      <nav className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 md:h-20 flex items-center justify-between">
        <a href="#home" data-testid="navbar-logo" className="flex items-center gap-2 shrink-0" onClick={close}>
          <img src="/assets/logo.png" alt="On Time Couriers" className="h-11 md:h-14 w-auto" />
        </a>

        <div className="hidden lg:flex items-center gap-8">
          {NAV_LINKS.map((l) => (
            <a
              key={l.href}
              href={l.href}
              data-testid={`navlink-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
              className="text-sm font-semibold text-foreground/80 hover:text-primary transition-colors"
            >
              {l.label}
            </a>
          ))}
        </div>

        <div className="hidden lg:flex items-center gap-4">
          <a href="tel:+18882714255" className="flex items-center gap-2 text-sm font-bold text-foreground hover:text-primary transition-colors">
            <Phone className="w-4 h-4 text-primary" /> (888) 271-4255
          </a>
          <a
            href="#contact"
            data-testid="navbar-cta"
            className="inline-flex items-center gap-2 rounded-full bg-primary px-5 py-2.5 text-sm font-semibold text-white shadow-lg shadow-primary/25 hover:bg-primary/90 hover:-translate-y-0.5 transition-all"
          >
            Get a Free Quote <ArrowRight className="w-4 h-4" />
          </a>
        </div>

        <button
          data-testid="mobile-menu-toggle"
          aria-label="Toggle menu"
          onClick={() => setOpen((v) => !v)}
          className="lg:hidden inline-flex items-center justify-center w-11 h-11 rounded-xl bg-white/80 border border-border text-foreground active:scale-95 transition-transform"
        >
          {open ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
        </button>
      </nav>

      {/* Mobile panel */}
      <div
        data-testid="mobile-menu-panel"
        className={`lg:hidden overflow-hidden bg-white border-b border-border transition-[max-height,opacity] duration-300 ${
          open ? "max-h-[480px] opacity-100" : "max-h-0 opacity-0"
        }`}
      >
        <div className="px-4 sm:px-6 py-4 flex flex-col gap-1">
          {NAV_LINKS.map((l) => (
            <a
              key={l.href}
              href={l.href}
              onClick={close}
              data-testid={`mobile-navlink-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
              className="py-3 px-3 rounded-lg text-base font-semibold text-foreground hover:bg-primary/5 hover:text-primary transition-colors"
            >
              {l.label}
            </a>
          ))}
          <a href="tel:+18882714255" className="py-3 px-3 flex items-center gap-2 text-base font-bold text-foreground">
            <Phone className="w-5 h-5 text-primary" /> (888) 271-4255
          </a>
          <a
            href="#contact"
            onClick={close}
            data-testid="mobile-navbar-cta"
            className="mt-2 inline-flex items-center justify-center gap-2 rounded-full bg-primary px-5 py-3.5 text-base font-semibold text-white shadow-lg shadow-primary/25"
          >
            Get a Free Quote <ArrowRight className="w-4 h-4" />
          </a>
        </div>
      </div>
    </header>
  );
};

export default Navbar;
